import React from "react";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * BrandLogo — single source of truth for the All Access wordmark/logo.
 *
 * Two authoritative assets ship in `/app/frontend/public/brand/`:
 *   - all-access-logo-light.png  → black-lettering wordmark  (light mode / light surfaces)
 *   - all-access-logo-dark.png   → yellow-lettering wordmark (dark  mode / dark  surfaces)
 *
 * We reference the public paths (not bundler imports) so the same URLs work
 * from generated PDFs without a bundler rewrite. Do NOT AI-regenerate the
 * logo; only the exact supplied assets ship.
 *
 * Theme selection rule:
 *   - Uses `resolvedTheme` (light or dark, with System resolved to actual OS pref).
 *   - `forceDark` prop overrides selection to the yellow variant. Used by
 *     surfaces that are dark chrome in BOTH themes (portal top nav / mobile
 *     top bar).
 *
 * The chrome-mist background chip that previously wrapped the dark-surface
 * variant has been REMOVED — the yellow-lettering asset reads natively on
 * dark chrome, so no chip is required.
 *
 * No CSS filters are used on either image. Both ship as-is.
 */

const LIGHT_LOGO_SRC = "/brand/all-access-logo-light.png";
const DARK_LOGO_SRC = "/brand/all-access-logo-dark.png";

// Assets are landscape wordmarks; light is 1278x323 (~3.96:1), dark is 400x120 (~3.33:1).
// We render height-based and let width auto so both variants scale from the
// same explicit height without layout shift on theme toggle. We reserve a
// stable rendered width using the LIGHT aspect ratio so switching themes
// never causes horizontal jitter.
const RENDER_ASPECT = 3.96;

/**
 * @param {"dark" | "light"} [surface]  legacy prop — retained for API compat
 *                                       but no longer affects rendering
 * @param {boolean} [forceDark]         force the yellow (dark) asset regardless of theme
 * @param {number} [height]              pixel height (aspect preserved)
 * @param {boolean} [withOpsMarker]      append the small OPS pill
 */
export const BrandLogo = ({
  surface = "light",
  forceDark = false,
  height = 36,
  fluid = false,
  withOpsMarker = true,
  className = "",
  testid = "brand-logo",
}) => {
  const { resolvedTheme } = useTheme();
  const useDark = forceDark || resolvedTheme === "dark";
  const src = useDark ? DARK_LOGO_SRC : LIGHT_LOGO_SRC;

  // Stable rendered width (based on light aspect) so the frame doesn't
  // reflow when the underlying asset swaps between themes. Ignored when
  // the component is `fluid` (fills its container while preserving
  // aspect via object-fit).
  const renderWidth = Math.round(height * RENDER_ASPECT);

  const fixedStyle = { height, width: renderWidth, objectFit: "contain" };
  const fluidStyle = {
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
  };

  return (
    <div
      className={
        fluid
          ? `flex items-center justify-center w-full h-full ${className}`
          : `inline-flex items-center gap-2 ${className}`
      }
      data-testid={testid}
    >
      <img
        src={src}
        alt="All Access Coach Leasing"
        style={fluid ? fluidStyle : fixedStyle}
        data-testid="brand-logo-img"
        data-variant={useDark ? "dark" : "light"}
        draggable={false}
      />
      {withOpsMarker && !fluid && (
        <span
          data-testid="ops-marker"
          className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9.5px] font-semibold tracking-widest border"
          style={{
            color: useDark ? "#B7E31D" : "#0C2F55",
            borderColor: useDark ? "#B7E31D" : "#1B4F8A",
            backgroundColor: useDark ? "rgba(183,227,29,0.10)" : "#FFFFFF",
          }}
        >
          OPS
        </span>
      )}
    </div>
  );
};

/**
 * Small helper used by index.html-side preload logic (kept exported so tests
 * or SSR paths can reference the exact filenames used at runtime).
 */
export const LOGO_SOURCES = { light: LIGHT_LOGO_SRC, dark: DARK_LOGO_SRC };
